/**
 * Embedding Space Explorer
 * Interactive visualization of word embeddings with similarity search and analogies
 */
import {
  computeAnalogyVector,
  findEmbeddingByWord,
  findNearestNeighbors,
  findNearestToVector,
  normalizeWord,
} from '../../lib/labs/embedding-explorer';

// ===== Types =====
interface WordEmbedding {
  word: string;
  category: string;
  vector: number[]; // 50-dimensional
  x: number; // 2D projection
  y: number;
}

type Mode = 'explore' | 'similar' | 'analogy';

// ===== Category Colors =====
const CATEGORY_COLORS: Record<string, string> = {
  animals: '#f97316', // orange
  emotions: '#ec4899', // pink
  colors: '#8b5cf6', // purple
  programming: '#22c55e', // green
  security: '#ef4444', // red
  food: '#eab308', // yellow
  countries: '#3b82f6', // blue
  science: '#06b6d4', // cyan
  custom: '#94a3b8', // gray
};

// ===== Pre-computed Embeddings =====
// Each word has a curated 50-dim demo vector and static 2D coordinates.
// This is educational sample data, not model output.
const EMBEDDINGS: WordEmbedding[] = [
  // Animals - cluster together
  {
    word: 'cat',
    category: 'animals',
    vector: [
      0.32, -0.15, 0.28, 0.41, -0.22, 0.18, 0.35, -0.29, 0.12, 0.44, -0.11, 0.33, 0.27,
      -0.38, 0.19, 0.42, -0.16, 0.31, 0.24, -0.35, 0.21, 0.39, -0.13, 0.36, 0.29, -0.26,
      0.17, 0.43, -0.19, 0.34, 0.22, -0.31, 0.15, 0.4, -0.23, 0.37, 0.26, -0.33, 0.2,
      0.45, -0.14, 0.32, 0.25, -0.36, 0.18, 0.41, -0.21, 0.38, 0.23, -0.28,
    ],
    x: -0.65,
    y: 0.42,
  },
  {
    word: 'dog',
    category: 'animals',
    vector: [
      0.31, -0.14, 0.29, 0.4, -0.21, 0.19, 0.34, -0.28, 0.13, 0.43, -0.12, 0.32, 0.28,
      -0.37, 0.2, 0.41, -0.17, 0.3, 0.25, -0.34, 0.22, 0.38, -0.14, 0.35, 0.3, -0.25,
      0.18, 0.42, -0.2, 0.33, 0.23, -0.3, 0.16, 0.39, -0.24, 0.36, 0.27, -0.32, 0.21,
      0.44, -0.15, 0.31, 0.26, -0.35, 0.19, 0.4, -0.22, 0.37, 0.24, -0.27,
    ],
    x: -0.58,
    y: 0.48,
  },
  {
    word: 'bird',
    category: 'animals',
    vector: [
      0.28, -0.18, 0.25, 0.38, -0.25, 0.15, 0.32, -0.32, 0.09, 0.41, -0.15, 0.29, 0.24,
      -0.41, 0.16, 0.39, -0.2, 0.27, 0.21, -0.38, 0.18, 0.36, -0.17, 0.33, 0.26, -0.29,
      0.14, 0.4, -0.23, 0.31, 0.19, -0.34, 0.12, 0.37, -0.27, 0.34, 0.23, -0.36, 0.17,
      0.42, -0.18, 0.28, 0.22, -0.39, 0.15, 0.38, -0.25, 0.35, 0.2, -0.31,
    ],
    x: -0.72,
    y: 0.55,
  },
  {
    word: 'fish',
    category: 'animals',
    vector: [
      0.25, -0.21, 0.22, 0.35, -0.28, 0.12, 0.29, -0.35, 0.06, 0.38, -0.18, 0.26, 0.21,
      -0.44, 0.13, 0.36, -0.23, 0.24, 0.18, -0.41, 0.15, 0.33, -0.2, 0.3, 0.23, -0.32,
      0.11, 0.37, -0.26, 0.28, 0.16, -0.37, 0.09, 0.34, -0.3, 0.31, 0.2, -0.39, 0.14,
      0.39, -0.21, 0.25, 0.19, -0.42, 0.12, 0.35, -0.28, 0.32, 0.17, -0.34,
    ],
    x: -0.8,
    y: 0.38,
  },
  {
    word: 'lion',
    category: 'animals',
    vector: [
      0.35, -0.12, 0.31, 0.44, -0.19, 0.22, 0.38, -0.26, 0.16, 0.47, -0.09, 0.36, 0.31,
      -0.35, 0.23, 0.45, -0.14, 0.34, 0.28, -0.32, 0.25, 0.42, -0.11, 0.39, 0.33, -0.23,
      0.21, 0.46, -0.17, 0.37, 0.26, -0.28, 0.19, 0.43, -0.21, 0.4, 0.3, -0.3, 0.24, 0.48,
      -0.12, 0.35, 0.29, -0.33, 0.22, 0.44, -0.19, 0.41, 0.27, -0.25,
    ],
    x: -0.52,
    y: 0.35,
  },
  {
    word: 'elephant',
    category: 'animals',
    vector: [
      0.33, -0.16, 0.3, 0.42, -0.23, 0.2, 0.36, -0.3, 0.14, 0.45, -0.13, 0.34, 0.29,
      -0.39, 0.21, 0.43, -0.18, 0.32, 0.26, -0.36, 0.23, 0.4, -0.15, 0.37, 0.31, -0.27,
      0.19, 0.44, -0.21, 0.35, 0.24, -0.32, 0.17, 0.41, -0.25, 0.38, 0.28, -0.34, 0.22,
      0.46, -0.16, 0.33, 0.27, -0.37, 0.2, 0.42, -0.23, 0.39, 0.25, -0.29,
    ],
    x: -0.6,
    y: 0.3,
  },
  {
    word: 'tiger',
    category: 'animals',
    vector: [
      0.34, -0.13, 0.3, 0.43, -0.2, 0.21, 0.37, -0.27, 0.15, 0.46, -0.1, 0.35, 0.3, -0.36,
      0.22, 0.44, -0.15, 0.33, 0.27, -0.33, 0.24, 0.41, -0.12, 0.38, 0.32, -0.24, 0.2,
      0.45, -0.18, 0.36, 0.25, -0.29, 0.18, 0.42, -0.22, 0.39, 0.29, -0.31, 0.23, 0.47,
      -0.13, 0.34, 0.28, -0.34, 0.21, 0.43, -0.2, 0.4, 0.26, -0.26,
    ],
    x: -0.55,
    y: 0.38,
  },
  {
    word: 'snake',
    category: 'animals',
    vector: [
      0.26, -0.2, 0.23, 0.36, -0.27, 0.13, 0.3, -0.34, 0.07, 0.39, -0.17, 0.27, 0.22,
      -0.43, 0.14, 0.37, -0.22, 0.25, 0.19, -0.4, 0.16, 0.34, -0.19, 0.31, 0.24, -0.31,
      0.12, 0.38, -0.25, 0.29, 0.17, -0.36, 0.1, 0.35, -0.29, 0.32, 0.21, -0.38, 0.15,
      0.4, -0.2, 0.26, 0.2, -0.41, 0.13, 0.36, -0.27, 0.33, 0.18, -0.33,
    ],
    x: -0.78,
    y: 0.45,
  },

  // Emotions - cluster together
  {
    word: 'happy',
    category: 'emotions',
    vector: [
      0.45, 0.32, -0.18, 0.28, 0.41, -0.12, 0.35, 0.22, -0.25, 0.39, 0.15, -0.31, 0.43,
      0.19, -0.22, 0.36, 0.28, -0.15, 0.42, 0.25, -0.19, 0.38, 0.21, -0.28, 0.44, 0.17,
      -0.24, 0.37, 0.3, -0.16, 0.41, 0.23, -0.21, 0.34, 0.27, -0.18, 0.4, 0.2, -0.26,
      0.33, 0.31, -0.14, 0.46, 0.18, -0.23, 0.35, 0.29, -0.17, 0.43, 0.24,
    ],
    x: 0.45,
    y: 0.62,
  },
  {
    word: 'sad',
    category: 'emotions',
    vector: [
      -0.42, 0.28, -0.15, 0.25, -0.38, 0.18, -0.32, 0.35, -0.22, 0.21, -0.45, 0.12, -0.29,
      0.39, -0.19, 0.31, -0.36, 0.24, -0.26, 0.42, -0.16, 0.27, -0.41, 0.2, -0.33, 0.37,
      -0.23, 0.29, -0.39, 0.14, -0.3, 0.43, -0.2, 0.33, -0.35, 0.26, -0.28, 0.4, -0.17,
      0.35, -0.44, 0.22, -0.31, 0.38, -0.21, 0.3, -0.37, 0.25, -0.34, 0.41,
    ],
    x: 0.52,
    y: 0.75,
  },
  {
    word: 'angry',
    category: 'emotions',
    vector: [
      -0.38, 0.22, 0.35, -0.28, 0.41, -0.15, 0.32, -0.45, 0.19, -0.25, 0.38, -0.12, 0.29,
      -0.42, 0.16, -0.31, 0.44, -0.21, 0.26, -0.36, 0.23, -0.33, 0.4, -0.18, 0.3, -0.39,
      0.14, -0.27, 0.43, -0.2, 0.34, -0.37, 0.17, -0.24, 0.41, -0.22, 0.28, -0.35, 0.21,
      -0.32, 0.39, -0.13, 0.31, -0.46, 0.18, -0.29, 0.42, -0.24, 0.27, -0.34,
    ],
    x: 0.58,
    y: 0.55,
  },
  {
    word: 'joyful',
    category: 'emotions',
    vector: [
      0.46, 0.33, -0.17, 0.29, 0.42, -0.11, 0.36, 0.23, -0.24, 0.4, 0.16, -0.3, 0.44, 0.2,
      -0.21, 0.37, 0.29, -0.14, 0.43, 0.26, -0.18, 0.39, 0.22, -0.27, 0.45, 0.18, -0.23,
      0.38, 0.31, -0.15, 0.42, 0.24, -0.2, 0.35, 0.28, -0.17, 0.41, 0.21, -0.25, 0.34,
      0.32, -0.13, 0.47, 0.19, -0.22, 0.36, 0.3, -0.16, 0.44, 0.25,
    ],
    x: 0.42,
    y: 0.65,
  },
  {
    word: 'fear',
    category: 'emotions',
    vector: [
      -0.35, 0.18, 0.28, -0.42, 0.32, -0.21, 0.25, -0.38, 0.15, -0.29, 0.45, -0.18, 0.22,
      -0.35, 0.12, -0.26, 0.41, -0.24, 0.19, -0.32, 0.38, -0.15, 0.27, -0.44, 0.21, -0.3,
      0.35, -0.19, 0.24, -0.37, 0.42, -0.22, 0.28, -0.33, 0.16, -0.27, 0.39, -0.25, 0.23,
      -0.36, 0.31, -0.14, 0.2, -0.43, 0.34, -0.28, 0.17, -0.31, 0.4, -0.2,
    ],
    x: 0.48,
    y: 0.7,
  },
  {
    word: 'love',
    category: 'emotions',
    vector: [
      0.48, 0.35, -0.12, 0.32, 0.44, -0.08, 0.38, 0.26, -0.19, 0.42, 0.19, -0.26, 0.46,
      0.23, -0.16, 0.39, 0.32, -0.11, 0.45, 0.29, -0.14, 0.41, 0.25, -0.22, 0.47, 0.21,
      -0.18, 0.4, 0.34, -0.12, 0.44, 0.27, -0.15, 0.37, 0.31, -0.13, 0.43, 0.24, -0.2,
      0.36, 0.35, -0.1, 0.49, 0.22, -0.17, 0.38, 0.33, -0.14, 0.46, 0.28,
    ],
    x: 0.38,
    y: 0.58,
  },
  {
    word: 'hate',
    category: 'emotions',
    vector: [
      -0.45, 0.15, 0.38, -0.32, 0.42, -0.18, 0.28, -0.48, 0.22, -0.35, 0.39, -0.14, 0.25,
      -0.44, 0.19, -0.38, 0.46, -0.25, 0.21, -0.41, 0.35, -0.17, 0.32, -0.47, 0.26, -0.34,
      0.4, -0.22, 0.29, -0.43, 0.36, -0.19, 0.23, -0.37, 0.44, -0.28, 0.31, -0.4, 0.17,
      -0.33, 0.41, -0.15, 0.27, -0.46, 0.38, -0.24, 0.2, -0.36, 0.43, -0.21,
    ],
    x: 0.62,
    y: 0.52,
  },
  {
    word: 'excited',
    category: 'emotions',
    vector: [
      0.44, 0.3, -0.19, 0.27, 0.4, -0.13, 0.34, 0.21, -0.26, 0.38, 0.14, -0.32, 0.42,
      0.18, -0.23, 0.35, 0.27, -0.16, 0.41, 0.24, -0.2, 0.37, 0.2, -0.29, 0.43, 0.16,
      -0.25, 0.36, 0.29, -0.17, 0.4, 0.22, -0.22, 0.33, 0.26, -0.19, 0.39, 0.19, -0.27,
      0.32, 0.3, -0.15, 0.45, 0.17, -0.24, 0.34, 0.28, -0.18, 0.42, 0.23,
    ],
    x: 0.5,
    y: 0.68,
  },

  // Colors - cluster together
  {
    word: 'red',
    category: 'colors',
    vector: [
      0.52, -0.28, 0.15, -0.38, 0.22, 0.45, -0.19, 0.32, -0.42, 0.12, 0.48, -0.25, 0.18,
      -0.35, 0.42, -0.15, 0.28, -0.39, 0.21, 0.46, -0.22, 0.35, -0.31, 0.14, 0.5, -0.18,
      0.25, -0.44, 0.19, 0.41, -0.26, 0.38, -0.33, 0.16, 0.47, -0.21, 0.31, -0.37, 0.23,
      0.43, -0.29, 0.34, -0.4, 0.13, 0.49, -0.24, 0.27, -0.36, 0.2, 0.44,
    ],
    x: -0.15,
    y: -0.55,
  },
  {
    word: 'blue',
    category: 'colors',
    vector: [
      0.18, -0.45, 0.32, -0.22, 0.48, 0.15, -0.38, 0.25, -0.12, 0.42, 0.28, -0.35, 0.19,
      -0.5, 0.35, -0.18, 0.45, -0.29, 0.22, 0.38, -0.42, 0.31, -0.15, 0.48, 0.12, -0.32,
      0.41, -0.25, 0.52, 0.18, -0.39, 0.28, -0.46, 0.21, 0.35, -0.22, 0.44, -0.31, 0.15,
      0.49, -0.28, 0.38, -0.19, 0.45, 0.25, -0.36, 0.32, -0.43, 0.19, 0.41,
    ],
    x: -0.22,
    y: -0.62,
  },
  {
    word: 'green',
    category: 'colors',
    vector: [
      0.35, -0.18, 0.42, -0.31, 0.15, 0.48, -0.25, 0.22, -0.38, 0.45, 0.12, -0.32, 0.28,
      -0.45, 0.19, 0.41, -0.22, 0.35, -0.15, 0.5, -0.28, 0.18, -0.42, 0.32, 0.25, -0.35,
      0.48, -0.19, 0.38, 0.15, -0.45, 0.29, -0.22, 0.42, 0.21, -0.31, 0.52, -0.25, 0.35,
      0.18, -0.39, 0.45, -0.28, 0.22, 0.32, -0.48, 0.19, -0.35, 0.41, 0.26,
    ],
    x: -0.08,
    y: -0.48,
  },
  {
    word: 'yellow',
    category: 'colors',
    vector: [
      0.48, -0.15, 0.22, -0.35, 0.38, 0.28, -0.42, 0.19, -0.25, 0.45, 0.32, -0.18, 0.41,
      -0.31, 0.15, 0.52, -0.22, 0.35, -0.28, 0.12, 0.48, -0.19, 0.25, -0.38, 0.42, -0.32,
      0.18, -0.45, 0.29, 0.35, -0.15, 0.41, -0.25, 0.22, 0.5, -0.28, 0.38, -0.19, 0.32,
      0.45, -0.22, 0.28, -0.35, 0.15, 0.48, -0.31, 0.21, -0.42, 0.39, 0.25,
    ],
    x: -0.05,
    y: -0.58,
  },
  {
    word: 'purple',
    category: 'colors',
    vector: [
      0.38, -0.32, 0.18, -0.45, 0.28, 0.42, -0.15, 0.35, -0.48, 0.22, 0.41, -0.25, 0.12,
      -0.38, 0.45, -0.19, 0.32, -0.42, 0.25, 0.48, -0.28, 0.38, -0.35, 0.15, 0.44, -0.22,
      0.29, -0.41, 0.18, 0.35, -0.32, 0.45, -0.38, 0.21, 0.42, -0.25, 0.28, -0.48, 0.19,
      0.38, -0.35, 0.32, -0.45, 0.15, 0.41, -0.28, 0.22, -0.39, 0.25, 0.48,
    ],
    x: -0.18,
    y: -0.45,
  },
  {
    word: 'orange',
    category: 'colors',
    vector: [
      0.5, -0.25, 0.18, -0.35, 0.32, 0.42, -0.22, 0.28, -0.45, 0.15, 0.48, -0.28, 0.21,
      -0.38, 0.38, -0.18, 0.25, -0.42, 0.35, 0.45, -0.19, 0.32, -0.35, 0.12, 0.52, -0.22,
      0.28, -0.48, 0.19, 0.41, -0.25, 0.35, -0.32, 0.15, 0.45, -0.28, 0.38, -0.42, 0.22,
      0.48, -0.31, 0.25, -0.38, 0.18, 0.42, -0.35, 0.29, -0.45, 0.21, 0.39,
    ],
    x: -0.12,
    y: -0.52,
  },
  {
    word: 'black',
    category: 'colors',
    vector: [
      -0.25, -0.42, 0.15, -0.35, -0.18, 0.28, -0.45, 0.22, -0.38, -0.12, 0.35, -0.48,
      0.19, -0.32, -0.25, 0.42, -0.15, -0.38, 0.28, -0.45, 0.18, -0.22, 0.35, -0.48,
      -0.19, 0.32, -0.42, 0.15, -0.28, 0.45, -0.35, -0.18, 0.25, -0.38, 0.48, -0.22,
      -0.32, 0.19, -0.45, 0.28, -0.12, 0.35, -0.42, -0.25, 0.38, -0.18, 0.48, -0.32,
      -0.15, 0.42,
    ],
    x: -0.28,
    y: -0.68,
  },
  {
    word: 'white',
    category: 'colors',
    vector: [
      0.22, -0.38, 0.45, -0.15, 0.32, -0.28, 0.48, -0.18, 0.35, -0.42, 0.12, -0.25, 0.41,
      -0.35, 0.19, -0.45, 0.28, -0.32, 0.38, -0.15, 0.22, -0.48, 0.35, -0.19, 0.42, -0.28,
      0.15, -0.38, 0.45, -0.22, 0.32, -0.42, 0.18, -0.35, 0.48, -0.25, 0.28, -0.45, 0.21,
      -0.32, 0.38, -0.18, 0.45, -0.28, 0.15, -0.42, 0.35, -0.22, 0.41, -0.38,
    ],
    x: -0.25,
    y: -0.42,
  },

  // Programming - cluster together
  {
    word: 'python',
    category: 'programming',
    vector: [
      0.15, 0.42, 0.28, 0.35, -0.45, 0.22, 0.38, 0.18, -0.32, 0.48, 0.25, 0.41, -0.19,
      0.35, 0.12, 0.45, 0.28, -0.38, 0.22, 0.32, 0.48, -0.15, 0.41, 0.25, -0.35, 0.18,
      0.42, 0.28, -0.45, 0.35, 0.22, 0.38, -0.25, 0.15, 0.48, 0.32, -0.42, 0.19, 0.45,
      0.28, -0.35, 0.22, 0.41, 0.18, -0.38, 0.25, 0.35, 0.42, -0.28, 0.15,
    ],
    x: 0.62,
    y: -0.35,
  },
  {
    word: 'javascript',
    category: 'programming',
    vector: [
      0.18, 0.45, 0.25, 0.38, -0.42, 0.19, 0.35, 0.22, -0.28, 0.48, 0.28, 0.44, -0.15,
      0.32, 0.15, 0.42, 0.31, -0.35, 0.25, 0.35, 0.45, -0.18, 0.38, 0.28, -0.32, 0.21,
      0.39, 0.25, -0.42, 0.38, 0.19, 0.35, -0.22, 0.18, 0.45, 0.28, -0.38, 0.22, 0.42,
      0.31, -0.32, 0.25, 0.38, 0.21, -0.35, 0.28, 0.32, 0.39, -0.25, 0.18,
    ],
    x: 0.68,
    y: -0.28,
  },
  {
    word: 'rust',
    category: 'programming',
    vector: [
      0.22, 0.38, 0.32, 0.42, -0.48, 0.25, 0.41, 0.15, -0.35, 0.45, 0.22, 0.38, -0.22,
      0.38, 0.18, 0.48, 0.25, -0.42, 0.19, 0.28, 0.52, -0.12, 0.44, 0.22, -0.38, 0.15,
      0.45, 0.32, -0.48, 0.32, 0.25, 0.41, -0.28, 0.12, 0.52, 0.35, -0.45, 0.16, 0.48,
      0.25, -0.38, 0.19, 0.44, 0.15, -0.42, 0.22, 0.38, 0.45, -0.32, 0.12,
    ],
    x: 0.72,
    y: -0.42,
  },
  {
    word: 'golang',
    category: 'programming',
    vector: [
      0.19, 0.41, 0.28, 0.39, -0.45, 0.21, 0.36, 0.19, -0.31, 0.47, 0.26, 0.42, -0.17,
      0.34, 0.14, 0.44, 0.29, -0.37, 0.23, 0.33, 0.47, -0.16, 0.4, 0.26, -0.34, 0.19,
      0.41, 0.27, -0.44, 0.36, 0.21, 0.37, -0.24, 0.16, 0.47, 0.3, -0.4, 0.2, 0.44, 0.29,
      -0.34, 0.23, 0.4, 0.19, -0.37, 0.26, 0.34, 0.41, -0.27, 0.16,
    ],
    x: 0.65,
    y: -0.38,
  },
  {
    word: 'java',
    category: 'programming',
    vector: [
      0.16, 0.43, 0.26, 0.36, -0.44, 0.2, 0.37, 0.2, -0.3, 0.46, 0.27, 0.43, -0.16, 0.33,
      0.13, 0.43, 0.3, -0.36, 0.24, 0.34, 0.46, -0.17, 0.39, 0.27, -0.33, 0.2, 0.4, 0.26,
      -0.43, 0.37, 0.2, 0.36, -0.23, 0.17, 0.46, 0.29, -0.39, 0.21, 0.43, 0.3, -0.33,
      0.24, 0.39, 0.2, -0.36, 0.27, 0.33, 0.4, -0.26, 0.17,
    ],
    x: 0.58,
    y: -0.32,
  },
  {
    word: 'function',
    category: 'programming',
    vector: [
      0.21, 0.38, 0.31, 0.4, -0.46, 0.24, 0.39, 0.17, -0.33, 0.48, 0.24, 0.4, -0.2, 0.36,
      0.16, 0.46, 0.27, -0.4, 0.21, 0.3, 0.5, -0.14, 0.42, 0.24, -0.36, 0.17, 0.43, 0.3,
      -0.46, 0.34, 0.23, 0.39, -0.26, 0.14, 0.5, 0.33, -0.43, 0.18, 0.46, 0.27, -0.36,
      0.21, 0.42, 0.17, -0.4, 0.24, 0.36, 0.43, -0.3, 0.14,
    ],
    x: 0.55,
    y: -0.45,
  },
  {
    word: 'variable',
    category: 'programming',
    vector: [
      0.2, 0.4, 0.29, 0.38, -0.44, 0.22, 0.37, 0.19, -0.31, 0.46, 0.25, 0.41, -0.18, 0.34,
      0.15, 0.44, 0.28, -0.38, 0.22, 0.32, 0.48, -0.15, 0.4, 0.25, -0.34, 0.18, 0.42,
      0.28, -0.44, 0.35, 0.22, 0.38, -0.25, 0.15, 0.48, 0.31, -0.41, 0.19, 0.45, 0.28,
      -0.35, 0.22, 0.41, 0.18, -0.38, 0.25, 0.35, 0.42, -0.28, 0.15,
    ],
    x: 0.52,
    y: -0.48,
  },
  {
    word: 'algorithm',
    category: 'programming',
    vector: [
      0.23, 0.36, 0.33, 0.41, -0.47, 0.26, 0.4, 0.16, -0.34, 0.49, 0.23, 0.39, -0.21,
      0.37, 0.17, 0.47, 0.26, -0.41, 0.2, 0.29, 0.51, -0.13, 0.43, 0.23, -0.37, 0.16,
      0.44, 0.31, -0.47, 0.33, 0.24, 0.4, -0.27, 0.13, 0.51, 0.34, -0.44, 0.17, 0.47,
      0.26, -0.37, 0.2, 0.43, 0.16, -0.41, 0.23, 0.37, 0.44, -0.31, 0.13,
    ],
    x: 0.48,
    y: -0.52,
  },

  // Security - cluster together
  {
    word: 'vulnerability',
    category: 'security',
    vector: [
      -0.35, 0.28, -0.42, 0.15, 0.38, -0.22, 0.45, -0.18, 0.32, -0.48, 0.25, -0.35, 0.19,
      0.42, -0.28, 0.12, 0.45, -0.35, 0.22, -0.38, 0.15, 0.48, -0.25, 0.32, -0.42, 0.18,
      -0.35, 0.28, 0.45, -0.22, 0.38, -0.15, 0.42, -0.32, 0.25, 0.48, -0.19, 0.35, -0.45,
      0.28, 0.22, -0.38, 0.15, 0.42, -0.32, 0.18, -0.45, 0.25, 0.35, -0.28,
    ],
    x: -0.42,
    y: -0.25,
  },
  {
    word: 'exploit',
    category: 'security',
    vector: [
      -0.38, 0.25, -0.45, 0.18, 0.35, -0.19, 0.42, -0.22, 0.28, -0.45, 0.22, -0.32, 0.15,
      0.38, -0.25, 0.18, 0.42, -0.32, 0.25, -0.35, 0.12, 0.45, -0.28, 0.35, -0.38, 0.15,
      -0.32, 0.25, 0.42, -0.19, 0.35, -0.18, 0.38, -0.28, 0.22, 0.45, -0.22, 0.32, -0.42,
      0.25, 0.19, -0.35, 0.12, 0.38, -0.28, 0.15, -0.42, 0.22, 0.32, -0.25,
    ],
    x: -0.48,
    y: -0.18,
  },
  {
    word: 'malware',
    category: 'security',
    vector: [
      -0.42, 0.22, -0.48, 0.15, 0.32, -0.25, 0.38, -0.18, 0.25, -0.42, 0.19, -0.35, 0.12,
      0.35, -0.22, 0.15, 0.38, -0.28, 0.22, -0.32, 0.18, 0.42, -0.25, 0.38, -0.35, 0.12,
      -0.28, 0.22, 0.38, -0.15, 0.32, -0.22, 0.35, -0.25, 0.18, 0.42, -0.18, 0.28, -0.38,
      0.22, 0.15, -0.32, 0.18, 0.35, -0.25, 0.12, -0.38, 0.19, 0.28, -0.22,
    ],
    x: -0.55,
    y: -0.12,
  },
  {
    word: 'encryption',
    category: 'security',
    vector: [
      0.28, 0.35, -0.38, 0.22, 0.45, -0.15, 0.32, 0.18, -0.42, 0.25, 0.38, -0.28, 0.15,
      0.42, -0.19, 0.32, 0.45, -0.22, 0.18, 0.35, -0.25, 0.42, -0.18, 0.28, 0.38, -0.32,
      0.22, 0.45, -0.15, 0.35, 0.19, 0.42, -0.28, 0.25, 0.38, -0.22, 0.32, 0.45, -0.18,
      0.28, -0.35, 0.15, 0.42, -0.25, 0.22, 0.38, -0.19, 0.35, 0.28, -0.32,
    ],
    x: -0.35,
    y: -0.32,
  },
  {
    word: 'firewall',
    category: 'security',
    vector: [
      0.25, 0.32, -0.35, 0.18, 0.42, -0.12, 0.28, 0.22, -0.38, 0.28, 0.35, -0.25, 0.18,
      0.38, -0.22, 0.28, 0.42, -0.18, 0.22, 0.32, -0.28, 0.38, -0.15, 0.25, 0.35, -0.28,
      0.18, 0.42, -0.12, 0.32, 0.22, 0.38, -0.25, 0.28, 0.35, -0.18, 0.28, 0.42, -0.22,
      0.25, -0.32, 0.18, 0.38, -0.22, 0.18, 0.35, -0.15, 0.32, 0.25, -0.28,
    ],
    x: -0.38,
    y: -0.38,
  },
  {
    word: 'attack',
    category: 'security',
    vector: [
      -0.4, 0.28, -0.45, 0.12, 0.38, -0.22, 0.42, -0.15, 0.28, -0.48, 0.25, -0.38, 0.18,
      0.42, -0.28, 0.15, 0.45, -0.35, 0.22, -0.42, 0.18, 0.48, -0.22, 0.32, -0.45, 0.15,
      -0.35, 0.28, 0.42, -0.18, 0.38, -0.12, 0.45, -0.28, 0.22, 0.48, -0.25, 0.35, -0.42,
      0.28, 0.18, -0.38, 0.12, 0.45, -0.28, 0.15, -0.48, 0.22, 0.35, -0.25,
    ],
    x: -0.52,
    y: -0.22,
  },
  {
    word: 'password',
    category: 'security',
    vector: [
      0.22, 0.28, -0.32, 0.15, 0.38, -0.18, 0.25, 0.35, -0.35, 0.32, 0.28, -0.22, 0.18,
      0.35, -0.25, 0.25, 0.38, -0.15, 0.28, 0.28, -0.32, 0.35, -0.18, 0.22, 0.32, -0.25,
      0.15, 0.38, -0.18, 0.28, 0.25, 0.35, -0.22, 0.32, 0.28, -0.15, 0.25, 0.38, -0.28,
      0.22, -0.28, 0.18, 0.35, -0.18, 0.15, 0.32, -0.22, 0.28, 0.22, -0.25,
    ],
    x: -0.32,
    y: -0.42,
  },
  {
    word: 'authentication',
    category: 'security',
    vector: [
      0.25, 0.3, -0.34, 0.18, 0.4, -0.15, 0.27, 0.32, -0.37, 0.3, 0.3, -0.24, 0.16, 0.37,
      -0.23, 0.27, 0.4, -0.17, 0.26, 0.3, -0.3, 0.37, -0.16, 0.24, 0.34, -0.27, 0.17, 0.4,
      -0.16, 0.3, 0.23, 0.37, -0.24, 0.3, 0.3, -0.17, 0.27, 0.4, -0.26, 0.24, -0.3, 0.16,
      0.37, -0.2, 0.17, 0.34, -0.2, 0.3, 0.24, -0.27,
    ],
    x: -0.28,
    y: -0.35,
  },

  // Food - cluster together
  {
    word: 'apple',
    category: 'food',
    vector: [
      0.38, 0.15, 0.42, -0.28, 0.22, -0.35, 0.18, 0.45, 0.25, -0.32, 0.12, 0.48, -0.22,
      0.35, 0.18, -0.42, 0.28, 0.38, -0.15, 0.22, 0.45, -0.25, 0.32, 0.18, -0.38, 0.42,
      0.15, -0.28, 0.35, 0.22, -0.45, 0.18, 0.32, -0.25, 0.48, 0.12, -0.35, 0.28, 0.38,
      -0.22, 0.42, 0.15, -0.32, 0.25, 0.35, -0.18, 0.45, 0.22, -0.28, 0.38,
    ],
    x: 0.15,
    y: 0.25,
  },
  {
    word: 'banana',
    category: 'food',
    vector: [
      0.35, 0.18, 0.38, -0.25, 0.25, -0.32, 0.22, 0.42, 0.28, -0.28, 0.15, 0.45, -0.18,
      0.32, 0.22, -0.38, 0.25, 0.35, -0.12, 0.18, 0.42, -0.22, 0.28, 0.22, -0.35, 0.38,
      0.18, -0.25, 0.32, 0.25, -0.42, 0.22, 0.28, -0.22, 0.45, 0.15, -0.32, 0.25, 0.35,
      -0.18, 0.38, 0.18, -0.28, 0.22, 0.32, -0.22, 0.42, 0.25, -0.25, 0.35,
    ],
    x: 0.22,
    y: 0.32,
  },
  {
    word: 'pizza',
    category: 'food',
    vector: [
      0.42, 0.22, 0.35, -0.32, 0.18, -0.38, 0.15, 0.48, 0.22, -0.35, 0.18, 0.42, -0.25,
      0.38, 0.12, -0.45, 0.32, 0.42, -0.18, 0.25, 0.48, -0.28, 0.35, 0.15, -0.42, 0.45,
      0.12, -0.32, 0.38, 0.18, -0.48, 0.22, 0.35, -0.28, 0.42, 0.18, -0.38, 0.32, 0.42,
      -0.25, 0.45, 0.12, -0.35, 0.28, 0.38, -0.22, 0.48, 0.18, -0.32, 0.42,
    ],
    x: 0.28,
    y: 0.18,
  },
  {
    word: 'burger',
    category: 'food',
    vector: [
      0.4, 0.25, 0.32, -0.35, 0.15, -0.42, 0.12, 0.45, 0.18, -0.38, 0.22, 0.38, -0.28,
      0.42, 0.15, -0.48, 0.35, 0.38, -0.22, 0.28, 0.45, -0.32, 0.38, 0.12, -0.45, 0.42,
      0.18, -0.35, 0.42, 0.15, -0.45, 0.25, 0.32, -0.32, 0.38, 0.22, -0.42, 0.35, 0.38,
      -0.28, 0.42, 0.15, -0.38, 0.32, 0.35, -0.25, 0.45, 0.22, -0.35, 0.38,
    ],
    x: 0.32,
    y: 0.15,
  },
  {
    word: 'pasta',
    category: 'food',
    vector: [
      0.36, 0.2, 0.38, -0.3, 0.2, -0.36, 0.18, 0.44, 0.24, -0.32, 0.16, 0.44, -0.22, 0.36,
      0.18, -0.42, 0.28, 0.38, -0.16, 0.22, 0.44, -0.26, 0.32, 0.18, -0.38, 0.4, 0.16,
      -0.28, 0.34, 0.22, -0.44, 0.2, 0.3, -0.26, 0.44, 0.16, -0.36, 0.28, 0.38, -0.22,
      0.4, 0.16, -0.32, 0.26, 0.34, -0.2, 0.44, 0.22, -0.28, 0.38,
    ],
    x: 0.25,
    y: 0.22,
  },
  {
    word: 'salad',
    category: 'food',
    vector: [
      0.32, 0.18, 0.42, -0.22, 0.28, -0.3, 0.24, 0.4, 0.3, -0.26, 0.18, 0.42, -0.16, 0.3,
      0.24, -0.36, 0.22, 0.34, -0.14, 0.16, 0.4, -0.2, 0.26, 0.24, -0.32, 0.36, 0.2,
      -0.22, 0.3, 0.28, -0.4, 0.24, 0.26, -0.2, 0.42, 0.18, -0.3, 0.22, 0.34, -0.16, 0.36,
      0.2, -0.26, 0.2, 0.3, -0.24, 0.4, 0.26, -0.22, 0.34,
    ],
    x: 0.18,
    y: 0.28,
  },
  {
    word: 'bread',
    category: 'food',
    vector: [
      0.34, 0.16, 0.4, -0.26, 0.24, -0.34, 0.2, 0.42, 0.26, -0.3, 0.14, 0.46, -0.2, 0.34,
      0.2, -0.4, 0.26, 0.36, -0.14, 0.2, 0.44, -0.24, 0.3, 0.2, -0.36, 0.4, 0.16, -0.26,
      0.34, 0.24, -0.44, 0.2, 0.28, -0.24, 0.46, 0.14, -0.34, 0.26, 0.36, -0.2, 0.4, 0.16,
      -0.3, 0.24, 0.34, -0.2, 0.44, 0.24, -0.26, 0.36,
    ],
    x: 0.2,
    y: 0.35,
  },
  {
    word: 'cheese',
    category: 'food',
    vector: [
      0.37, 0.19, 0.39, -0.27, 0.23, -0.33, 0.19, 0.43, 0.25, -0.31, 0.15, 0.45, -0.21,
      0.33, 0.19, -0.41, 0.27, 0.37, -0.15, 0.21, 0.43, -0.25, 0.31, 0.19, -0.37, 0.41,
      0.17, -0.27, 0.35, 0.23, -0.43, 0.21, 0.29, -0.25, 0.45, 0.15, -0.33, 0.27, 0.37,
      -0.21, 0.39, 0.17, -0.31, 0.25, 0.33, -0.21, 0.43, 0.23, -0.27, 0.37,
    ],
    x: 0.12,
    y: 0.3,
  },

  // Countries - cluster together
  {
    word: 'france',
    category: 'countries',
    vector: [
      0.28, -0.35, 0.42, 0.18, -0.22, 0.45, 0.15, -0.38, 0.32, 0.25, -0.42, 0.12, 0.35,
      -0.28, 0.48, 0.18, -0.32, 0.22, 0.38, -0.15, 0.45, 0.25, -0.35, 0.18, 0.42, -0.22,
      0.28, 0.48, -0.32, 0.15, 0.38, -0.25, 0.35, 0.22, -0.42, 0.18, 0.32, -0.38, 0.45,
      0.12, -0.28, 0.35, 0.25, -0.45, 0.22, 0.38, -0.18, 0.42, 0.28, -0.32,
    ],
    x: -0.35,
    y: 0.65,
  },
  {
    word: 'germany',
    category: 'countries',
    vector: [
      0.32, -0.38, 0.45, 0.15, -0.25, 0.42, 0.18, -0.35, 0.28, 0.22, -0.45, 0.15, 0.38,
      -0.32, 0.45, 0.22, -0.28, 0.25, 0.35, -0.18, 0.42, 0.28, -0.38, 0.15, 0.45, -0.25,
      0.32, 0.45, -0.35, 0.18, 0.35, -0.22, 0.38, 0.25, -0.45, 0.22, 0.28, -0.42, 0.42,
      0.15, -0.32, 0.38, 0.22, -0.48, 0.25, 0.35, -0.22, 0.45, 0.25, -0.28,
    ],
    x: -0.28,
    y: 0.72,
  },
  {
    word: 'japan',
    category: 'countries',
    vector: [
      0.35, -0.32, 0.38, 0.22, -0.18, 0.48, 0.12, -0.42, 0.35, 0.18, -0.38, 0.18, 0.32,
      -0.25, 0.52, 0.15, -0.35, 0.18, 0.42, -0.22, 0.48, 0.22, -0.32, 0.22, 0.38, -0.18,
      0.35, 0.42, -0.38, 0.22, 0.32, -0.28, 0.42, 0.18, -0.48, 0.15, 0.35, -0.35, 0.48,
      0.18, -0.25, 0.32, 0.28, -0.42, 0.18, 0.42, -0.15, 0.38, 0.32, -0.35,
    ],
    x: -0.42,
    y: 0.58,
  },
  {
    word: 'brazil',
    category: 'countries',
    vector: [
      0.3, -0.36, 0.4, 0.2, -0.2, 0.46, 0.14, -0.4, 0.3, 0.24, -0.4, 0.14, 0.36, -0.3,
      0.5, 0.2, -0.3, 0.2, 0.4, -0.16, 0.46, 0.26, -0.36, 0.2, 0.4, -0.24, 0.3, 0.46,
      -0.36, 0.16, 0.36, -0.26, 0.4, 0.2, -0.46, 0.2, 0.3, -0.4, 0.46, 0.14, -0.3, 0.36,
      0.24, -0.46, 0.2, 0.4, -0.2, 0.44, 0.26, -0.3,
    ],
    x: -0.5,
    y: 0.68,
  },
  {
    word: 'india',
    category: 'countries',
    vector: [
      0.33, -0.34, 0.41, 0.19, -0.21, 0.47, 0.13, -0.41, 0.33, 0.21, -0.39, 0.17, 0.34,
      -0.27, 0.51, 0.17, -0.33, 0.19, 0.41, -0.19, 0.47, 0.24, -0.34, 0.21, 0.39, -0.21,
      0.33, 0.44, -0.37, 0.19, 0.34, -0.25, 0.41, 0.19, -0.47, 0.17, 0.33, -0.37, 0.47,
      0.16, -0.27, 0.34, 0.26, -0.44, 0.19, 0.41, -0.17, 0.43, 0.29, -0.33,
    ],
    x: -0.38,
    y: 0.75,
  },
  {
    word: 'china',
    category: 'countries',
    vector: [
      0.36, -0.3, 0.39, 0.23, -0.17, 0.49, 0.11, -0.43, 0.36, 0.17, -0.37, 0.19, 0.31,
      -0.24, 0.53, 0.14, -0.36, 0.17, 0.43, -0.23, 0.49, 0.21, -0.31, 0.23, 0.37, -0.17,
      0.36, 0.41, -0.39, 0.23, 0.31, -0.29, 0.43, 0.17, -0.49, 0.14, 0.36, -0.34, 0.49,
      0.19, -0.24, 0.31, 0.29, -0.41, 0.17, 0.43, -0.14, 0.37, 0.33, -0.36,
    ],
    x: -0.45,
    y: 0.62,
  },
  {
    word: 'russia',
    category: 'countries',
    vector: [
      0.31, -0.37, 0.43, 0.16, -0.23, 0.44, 0.16, -0.37, 0.29, 0.23, -0.43, 0.13, 0.37,
      -0.29, 0.47, 0.19, -0.31, 0.23, 0.37, -0.17, 0.44, 0.27, -0.37, 0.17, 0.43, -0.23,
      0.31, 0.47, -0.34, 0.17, 0.37, -0.23, 0.37, 0.23, -0.44, 0.19, 0.29, -0.43, 0.44,
      0.13, -0.29, 0.37, 0.23, -0.47, 0.23, 0.37, -0.19, 0.43, 0.27, -0.31,
    ],
    x: -0.32,
    y: 0.8,
  },
  {
    word: 'australia',
    category: 'countries',
    vector: [
      0.29, -0.33, 0.37, 0.21, -0.19, 0.45, 0.15, -0.39, 0.31, 0.23, -0.41, 0.15, 0.35,
      -0.31, 0.49, 0.21, -0.29, 0.21, 0.39, -0.15, 0.45, 0.25, -0.35, 0.19, 0.41, -0.23,
      0.29, 0.45, -0.35, 0.17, 0.35, -0.25, 0.39, 0.21, -0.45, 0.21, 0.31, -0.39, 0.45,
      0.15, -0.31, 0.35, 0.25, -0.45, 0.21, 0.39, -0.19, 0.41, 0.27, -0.29,
    ],
    x: -0.25,
    y: 0.68,
  },

  // Science - cluster together
  {
    word: 'atom',
    category: 'science',
    vector: [
      0.18, 0.42, -0.28, 0.35, 0.22, -0.38, 0.45, 0.15, -0.32, 0.25, 0.48, -0.18, 0.35,
      0.28, -0.42, 0.12, 0.38, 0.22, -0.35, 0.45, 0.18, -0.28, 0.32, 0.42, -0.15, 0.38,
      0.25, -0.45, 0.22, 0.35, -0.32, 0.48, 0.18, -0.28, 0.42, 0.22, -0.38, 0.15, 0.45,
      -0.25, 0.32, 0.35, -0.22, 0.48, 0.18, -0.35, 0.28, 0.42, -0.18, 0.38,
    ],
    x: 0.72,
    y: 0.42,
  },
  {
    word: 'molecule',
    category: 'science',
    vector: [
      0.22, 0.45, -0.25, 0.38, 0.18, -0.42, 0.48, 0.12, -0.35, 0.28, 0.45, -0.22, 0.32,
      0.32, -0.38, 0.15, 0.42, 0.18, -0.32, 0.48, 0.22, -0.25, 0.35, 0.38, -0.18, 0.42,
      0.22, -0.48, 0.25, 0.38, -0.28, 0.45, 0.15, -0.32, 0.38, 0.25, -0.35, 0.18, 0.42,
      -0.22, 0.35, 0.38, -0.18, 0.45, 0.22, -0.38, 0.25, 0.45, -0.22, 0.35,
    ],
    x: 0.78,
    y: 0.38,
  },
  {
    word: 'electron',
    category: 'science',
    vector: [
      0.15, 0.38, -0.32, 0.32, 0.25, -0.35, 0.42, 0.18, -0.28, 0.22, 0.52, -0.15, 0.38,
      0.25, -0.45, 0.18, 0.35, 0.25, -0.38, 0.42, 0.15, -0.32, 0.28, 0.45, -0.12, 0.35,
      0.28, -0.42, 0.18, 0.32, -0.35, 0.52, 0.22, -0.25, 0.38, 0.18, -0.42, 0.12, 0.48,
      -0.28, 0.35, 0.32, -0.25, 0.45, 0.15, -0.32, 0.32, 0.38, -0.15, 0.42,
    ],
    x: 0.68,
    y: 0.48,
  },
  {
    word: 'gravity',
    category: 'science',
    vector: [
      0.25, 0.35, -0.38, 0.28, 0.32, -0.25, 0.42, 0.22, -0.18, 0.35, 0.45, -0.28, 0.25,
      0.38, -0.32, 0.22, 0.32, 0.28, -0.42, 0.38, 0.25, -0.18, 0.35, 0.35, -0.28, 0.32,
      0.32, -0.38, 0.28, 0.28, -0.42, 0.45, 0.22, -0.18, 0.35, 0.28, -0.32, 0.25, 0.38,
      -0.35, 0.28, 0.42, -0.22, 0.35, 0.28, -0.28, 0.38, 0.32, -0.25, 0.35,
    ],
    x: 0.65,
    y: 0.52,
  },
  {
    word: 'energy',
    category: 'science',
    vector: [
      0.28, 0.38, -0.35, 0.32, 0.28, -0.28, 0.45, 0.18, -0.22, 0.32, 0.42, -0.32, 0.28,
      0.42, -0.28, 0.18, 0.35, 0.32, -0.38, 0.42, 0.22, -0.22, 0.38, 0.32, -0.32, 0.35,
      0.28, -0.42, 0.32, 0.32, -0.38, 0.42, 0.18, -0.22, 0.38, 0.32, -0.28, 0.22, 0.42,
      -0.32, 0.32, 0.38, -0.18, 0.38, 0.32, -0.32, 0.35, 0.38, -0.22, 0.38,
    ],
    x: 0.75,
    y: 0.45,
  },
  {
    word: 'physics',
    category: 'science',
    vector: [
      0.22, 0.4, -0.3, 0.34, 0.24, -0.32, 0.44, 0.16, -0.26, 0.28, 0.48, -0.24, 0.32,
      0.34, -0.36, 0.16, 0.36, 0.26, -0.4, 0.44, 0.2, -0.24, 0.34, 0.38, -0.24, 0.36,
      0.28, -0.44, 0.24, 0.34, -0.36, 0.48, 0.2, -0.24, 0.4, 0.24, -0.34, 0.18, 0.44,
      -0.28, 0.32, 0.36, -0.2, 0.44, 0.24, -0.34, 0.3, 0.4, -0.2, 0.38,
    ],
    x: 0.7,
    y: 0.55,
  },
  {
    word: 'chemistry',
    category: 'science',
    vector: [
      0.2, 0.44, -0.26, 0.36, 0.2, -0.36, 0.46, 0.14, -0.3, 0.26, 0.46, -0.2, 0.34, 0.3,
      -0.4, 0.14, 0.4, 0.2, -0.34, 0.46, 0.24, -0.26, 0.32, 0.4, -0.16, 0.4, 0.24, -0.46,
      0.2, 0.36, -0.32, 0.46, 0.16, -0.28, 0.36, 0.26, -0.36, 0.16, 0.42, -0.24, 0.34,
      0.36, -0.16, 0.46, 0.2, -0.36, 0.26, 0.44, -0.2, 0.36,
    ],
    x: 0.82,
    y: 0.35,
  },
  {
    word: 'biology',
    category: 'science',
    vector: [
      0.24, 0.42, -0.28, 0.34, 0.22, -0.34, 0.44, 0.17, -0.28, 0.27, 0.47, -0.22, 0.33,
      0.32, -0.38, 0.16, 0.38, 0.23, -0.36, 0.45, 0.21, -0.25, 0.33, 0.39, -0.19, 0.38,
      0.26, -0.45, 0.22, 0.35, -0.34, 0.47, 0.18, -0.26, 0.38, 0.25, -0.35, 0.17, 0.43,
      -0.26, 0.33, 0.37, -0.18, 0.45, 0.22, -0.35, 0.28, 0.42, -0.19, 0.37,
    ],
    x: 0.85,
    y: 0.48,
  },

  // Royalty analogies
  {
    word: 'king',
    category: 'custom',
    vector: [
      0.45, -0.22, 0.38, 0.28, -0.15, 0.42, -0.32, 0.18, 0.35, -0.25, 0.48, -0.12, 0.32,
      0.22, -0.38, 0.45, -0.28, 0.15, 0.42, -0.35, 0.18, 0.52, -0.22, 0.28, 0.38, -0.18,
      0.45, -0.32, 0.25, 0.35, -0.15, 0.48, -0.28, 0.22, 0.42, -0.25, 0.32, 0.45, -0.18,
      0.38, -0.35, 0.15, 0.52, -0.22, 0.28, 0.42, -0.32, 0.18, 0.48, -0.25,
    ],
    x: 0.15,
    y: -0.15,
  },
  {
    word: 'queen',
    category: 'custom',
    vector: [
      0.42, -0.25, 0.35, 0.32, -0.18, 0.45, -0.28, 0.22, 0.38, -0.22, 0.52, -0.15, 0.28,
      0.25, -0.42, 0.48, -0.32, 0.18, 0.38, -0.38, 0.22, 0.48, -0.25, 0.32, 0.35, -0.22,
      0.42, -0.28, 0.28, 0.38, -0.18, 0.45, -0.32, 0.25, 0.38, -0.28, 0.28, 0.48, -0.22,
      0.35, -0.38, 0.18, 0.48, -0.25, 0.32, 0.38, -0.28, 0.22, 0.45, -0.22,
    ],
    x: 0.22,
    y: -0.08,
  },
  {
    word: 'man',
    category: 'custom',
    vector: [
      0.35, -0.18, 0.28, 0.42, -0.25, 0.32, -0.38, 0.22, 0.25, -0.35, 0.42, -0.22, 0.38,
      0.15, -0.28, 0.35, -0.42, 0.25, 0.32, -0.28, 0.18, 0.45, -0.32, 0.22, 0.28, -0.25,
      0.38, -0.18, 0.35, 0.25, -0.22, 0.42, -0.35, 0.18, 0.32, -0.38, 0.28, 0.35, -0.25,
      0.28, -0.42, 0.22, 0.45, -0.18, 0.35, 0.32, -0.28, 0.25, 0.38, -0.32,
    ],
    x: 0.08,
    y: -0.22,
  },
  {
    word: 'woman',
    category: 'custom',
    vector: [
      0.32, -0.22, 0.25, 0.45, -0.28, 0.35, -0.35, 0.25, 0.28, -0.32, 0.45, -0.25, 0.35,
      0.18, -0.32, 0.38, -0.45, 0.28, 0.28, -0.32, 0.22, 0.42, -0.35, 0.25, 0.25, -0.28,
      0.35, -0.22, 0.38, 0.28, -0.25, 0.38, -0.38, 0.22, 0.28, -0.42, 0.32, 0.32, -0.28,
      0.32, -0.38, 0.25, 0.42, -0.22, 0.38, 0.28, -0.32, 0.28, 0.35, -0.35,
    ],
    x: 0.18,
    y: -0.18,
  },
  {
    word: 'prince',
    category: 'custom',
    vector: [
      0.43, -0.2, 0.36, 0.3, -0.16, 0.43, -0.3, 0.2, 0.33, -0.23, 0.5, -0.13, 0.3, 0.23,
      -0.4, 0.46, -0.3, 0.16, 0.4, -0.36, 0.2, 0.5, -0.23, 0.3, 0.36, -0.2, 0.43, -0.3,
      0.26, 0.36, -0.16, 0.46, -0.3, 0.23, 0.4, -0.26, 0.3, 0.46, -0.2, 0.36, -0.36, 0.16,
      0.5, -0.23, 0.3, 0.4, -0.3, 0.2, 0.46, -0.23,
    ],
    x: 0.25,
    y: -0.12,
  },
  {
    word: 'princess',
    category: 'custom',
    vector: [
      0.4, -0.23, 0.33, 0.33, -0.19, 0.46, -0.26, 0.23, 0.36, -0.2, 0.53, -0.16, 0.26,
      0.26, -0.43, 0.5, -0.33, 0.19, 0.36, -0.4, 0.23, 0.46, -0.26, 0.33, 0.33, -0.23,
      0.4, -0.26, 0.3, 0.4, -0.19, 0.43, -0.33, 0.26, 0.36, -0.3, 0.26, 0.5, -0.23, 0.33,
      -0.4, 0.19, 0.46, -0.26, 0.33, 0.36, -0.26, 0.23, 0.43, -0.2,
    ],
    x: 0.3,
    y: -0.05,
  },
];

// ===== Presets =====
const PRESETS: Record<string, string[]> = {
  animals: ['cat', 'dog', 'bird', 'fish', 'lion', 'elephant', 'tiger', 'snake'],
  emotions: ['happy', 'sad', 'angry', 'joyful', 'fear', 'love', 'hate', 'excited'],
  colors: ['red', 'blue', 'green', 'yellow', 'purple', 'orange', 'black', 'white'],
  programming: [
    'python',
    'javascript',
    'rust',
    'golang',
    'java',
    'function',
    'variable',
    'algorithm',
  ],
  security: [
    'vulnerability',
    'exploit',
    'malware',
    'encryption',
    'firewall',
    'attack',
    'password',
    'authentication',
  ],
  food: ['apple', 'banana', 'pizza', 'burger', 'pasta', 'salad', 'bread', 'cheese'],
  countries: [
    'france',
    'germany',
    'japan',
    'brazil',
    'india',
    'china',
    'russia',
    'australia',
  ],
  science: [
    'atom',
    'molecule',
    'electron',
    'gravity',
    'energy',
    'physics',
    'chemistry',
    'biology',
  ],
};

// ===== Main Initialization =====
const root = document.querySelector('[data-embedding-explorer]');
if (root) {
  const canvas = document.getElementById('embedding-canvas') as HTMLCanvasElement;
  const ctx = canvas.getContext('2d')!;

  // State
  let activeEmbeddings: WordEmbedding[] = [];
  let selectedWord: WordEmbedding | null = null;
  let hoveredWord: WordEmbedding | null = null;
  let mode: Mode = 'explore';
  let showLabels = true;
  let showConnections = false;
  let colorByCategory = true;
  const loadedPresets = new Set<string>();

  // View transform
  let scale = 200;
  let offsetX = 0;
  let offsetY = 0;
  let isDragging = false;
  let dragStartX = 0;
  let dragStartY = 0;

  // DOM Elements
  const wordInput = document.getElementById('word-input') as HTMLInputElement;
  const addWordBtn = document.getElementById('add-word') as HTMLButtonElement;
  const clearAllBtn = document.getElementById('clear-all') as HTMLButtonElement;
  const resetViewBtn = document.getElementById('reset-view') as HTMLButtonElement;
  const wordFeedback = document.getElementById('word-feedback') as HTMLElement;
  const pointCount = document.getElementById('point-count') as HTMLElement;
  const tooltip = document.getElementById('hover-tooltip') as HTMLElement;
  const tooltipWord = document.getElementById('tooltip-word') as HTMLElement;
  const tooltipCategory = document.getElementById('tooltip-category') as HTMLElement;
  const selectedWordEl = document.getElementById('selected-word') as HTMLElement;
  const selectedCategoryEl = document.getElementById('selected-category') as HTMLElement;
  const neighborsList = document.getElementById('neighbors-list') as HTMLElement;
  const vectorPreview = document.getElementById('vector-preview') as HTMLElement;
  const categoryLegend = document.getElementById('category-legend') as HTMLElement;
  const analogyPanel = document.getElementById('analogy-panel') as HTMLElement;
  const analogyA = document.getElementById('analogy-a') as HTMLInputElement;
  const analogyB = document.getElementById('analogy-b') as HTMLInputElement;
  const analogyC = document.getElementById('analogy-c') as HTMLInputElement;
  const computeAnalogyBtn = document.getElementById(
    'compute-analogy'
  ) as HTMLButtonElement;
  const analogyResult = document.getElementById('analogy-result') as HTMLElement;
  const showLabelsCheckbox = document.getElementById('show-labels') as HTMLInputElement;
  const showConnectionsCheckbox = document.getElementById(
    'show-connections'
  ) as HTMLInputElement;
  const colorByCategoryCheckbox = document.getElementById(
    'color-by-category'
  ) as HTMLInputElement;
  const presetButtons = root.querySelectorAll('.preset-btn');

  // Canvas sizing
  function resizeCanvas() {
    const rect = canvas.parentElement!.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = 500;
    render();
  }

  // Coordinate transforms
  function worldToScreen(x: number, y: number): { x: number; y: number } {
    return {
      x: canvas.width / 2 + x * scale + offsetX,
      y: canvas.height / 2 - y * scale + offsetY,
    };
  }

  // Find word at screen position
  function findWordAtPosition(sx: number, sy: number): WordEmbedding | null {
    const radius = 8;
    for (const emb of activeEmbeddings) {
      const pos = worldToScreen(emb.x, emb.y);
      const dist = Math.sqrt((pos.x - sx) ** 2 + (pos.y - sy) ** 2);
      if (dist < radius + 5) return emb;
    }
    return null;
  }

  // Render
  function render() {
    ctx.fillStyle = '#0d1117';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const neighborSet =
      mode === 'similar' && selectedWord
        ? new Set(
            findNearestNeighbors(selectedWord, activeEmbeddings, 5).map(
              (n) => n.word.word
            )
          )
        : null;

    // Grid
    ctx.strokeStyle = '#21262d';
    ctx.lineWidth = 1;
    const gridStep = 0.2;
    for (let x = -2; x <= 2; x += gridStep) {
      const p1 = worldToScreen(x, -2);
      const p2 = worldToScreen(x, 2);
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.stroke();
    }
    for (let y = -2; y <= 2; y += gridStep) {
      const p1 = worldToScreen(-2, y);
      const p2 = worldToScreen(2, y);
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.stroke();
    }

    // Connections to selected word
    if (showConnections && selectedWord) {
      const neighbors = findNearestNeighbors(selectedWord, activeEmbeddings, 5);
      const selPos = worldToScreen(selectedWord.x, selectedWord.y);
      for (const n of neighbors) {
        const nPos = worldToScreen(n.word.x, n.word.y);
        const alpha = n.similarity * 0.5;
        ctx.strokeStyle = `rgba(139, 92, 246, ${alpha})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(selPos.x, selPos.y);
        ctx.lineTo(nPos.x, nPos.y);
        ctx.stroke();
      }
    }

    // Points
    for (const emb of activeEmbeddings) {
      const pos = worldToScreen(emb.x, emb.y);
      const color = colorByCategory
        ? CATEGORY_COLORS[emb.category] || CATEGORY_COLORS.custom
        : '#8b5cf6';
      const isSelected = selectedWord?.word === emb.word;
      const isHovered = hoveredWord?.word === emb.word;
      const isNeighbor = neighborSet?.has(emb.word) ?? false;

      // Glow for selected/hovered
      if (isSelected || isHovered || isNeighbor) {
        ctx.beginPath();
        const glowAlpha = isSelected ? 0.3 : isNeighbor ? 0.2 : 0.15;
        ctx.arc(pos.x, pos.y, 15, 0, Math.PI * 2);
        ctx.fillStyle = isSelected
          ? `rgba(139, 92, 246, ${glowAlpha})`
          : `rgba(255, 255, 255, ${glowAlpha})`;
        ctx.fill();
      }

      // Point
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, isSelected ? 10 : 7, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();

      if (isSelected || isNeighbor) {
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = isSelected ? 2 : 1.5;
        ctx.stroke();
      }

      // Label
      if (showLabels) {
        ctx.font = '11px system-ui, sans-serif';
        ctx.fillStyle = isSelected || isNeighbor ? '#fff' : '#8b949e';
        ctx.textAlign = 'center';
        ctx.fillText(emb.word, pos.x, pos.y - 14);
      }
    }

    // Update point count
    pointCount.textContent = `${activeEmbeddings.length} word${activeEmbeddings.length !== 1 ? 's' : ''}`;
  }

  // Update info panel
  function updateInfoPanel() {
    if (selectedWord) {
      selectedWordEl.textContent = selectedWord.word;
      selectedCategoryEl.textContent = selectedWord.category;

      const neighbors = findNearestNeighbors(selectedWord, activeEmbeddings, 5);
      neighborsList.innerHTML = neighbors
        .map(
          (n) =>
            `<button class="neighbor-tag" data-word="${n.word.word}">${n.word.word} <span class="similarity">${n.similarity.toFixed(2)}</span></button>`
        )
        .join('');

      // Add click handlers for neighbor tags
      neighborsList.querySelectorAll('.neighbor-tag').forEach((btn) => {
        btn.addEventListener('click', () => {
          const word = btn.getAttribute('data-word');
          const emb = activeEmbeddings.find((e) => e.word === word);
          if (emb) {
            selectedWord = emb;
            updateInfoPanel();
            render();
          }
        });
      });

      // Vector preview (show first 8 dims)
      const preview = selectedWord.vector
        .slice(0, 8)
        .map((v) => v.toFixed(3))
        .join(', ');
      vectorPreview.textContent = `[ ${preview}, ... ]`;
    } else {
      selectedWordEl.textContent = 'Click a point to select';
      selectedCategoryEl.textContent = '-';
      neighborsList.innerHTML =
        '<span class="text-sm text-muted">Select a word to see neighbors</span>';
      vectorPreview.textContent = '[ select a word to see its embedding vector ]';
    }
  }

  // Update legend
  function updateLegend() {
    const categories = new Set(activeEmbeddings.map((e) => e.category));
    categoryLegend.innerHTML = Array.from(categories)
      .map(
        (cat) =>
          `<span class="legend-item"><span class="legend-dot" style="background: ${CATEGORY_COLORS[cat] || CATEGORY_COLORS.custom}"></span>${cat}</span>`
      )
      .join('');
  }

  // Add word
  function setWordFeedback(message: string, tone: 'muted' | 'error' = 'muted') {
    if (!wordFeedback) return;
    wordFeedback.textContent = message;
    wordFeedback.classList.toggle('text-rose-400', tone === 'error');
    wordFeedback.classList.toggle('text-muted', tone === 'muted');
  }

  function addWord(word: string): boolean {
    const normalized = normalizeWord(word);
    if (!normalized) return false;

    // Check if already added
    if (activeEmbeddings.find((e) => e.word === normalized)) return true;

    // Find in embeddings
    const emb = findEmbeddingByWord(normalized, EMBEDDINGS);
    if (emb) {
      activeEmbeddings.push(emb);
      updateLegend();
      render();
      setWordFeedback('', 'muted');
      return true;
    } else {
      setWordFeedback(
        `"${normalized}" not in this demo vocabulary. Try a preset word.`,
        'error'
      );
      return false;
    }
  }

  // Load preset
  function loadPreset(preset: string) {
    const words = PRESETS[preset];
    if (!words) return;

    for (const word of words) {
      addWord(word);
    }

    loadedPresets.add(preset);
    updatePresetButtons();
  }

  function updatePresetButtons() {
    presetButtons.forEach((btn) => {
      const preset = btn.getAttribute('data-preset') || '';
      btn.classList.toggle('active', loadedPresets.has(preset));
    });
  }

  let showConnectionsBeforeSimilar = showConnections;

  function setMode(nextMode: Mode) {
    if (nextMode === 'similar' && mode !== 'similar') {
      showConnectionsBeforeSimilar = showConnections;
      showConnections = true;
      showConnectionsCheckbox.checked = true;
    } else if (mode === 'similar' && nextMode !== 'similar') {
      showConnections = showConnectionsBeforeSimilar;
      showConnectionsCheckbox.checked = showConnections;
    }
    mode = nextMode;
    analogyPanel.classList.toggle('hidden', mode !== 'analogy');
    render();
  }

  // Compute analogy
  function computeAnalogy() {
    const a = normalizeWord(analogyA.value);
    const b = normalizeWord(analogyB.value);
    const c = normalizeWord(analogyC.value);

    const embA = findEmbeddingByWord(a, EMBEDDINGS);
    const embB = findEmbeddingByWord(b, EMBEDDINGS);
    const embC = findEmbeddingByWord(c, EMBEDDINGS);

    if (!embA || !embB || !embC) {
      analogyResult.textContent = 'One or more words not found in vocabulary';
      return;
    }

    // A - B + C
    const resultVector = computeAnalogyVector(embA.vector, embB.vector, embC.vector);
    const nearest = findNearestToVector(resultVector, EMBEDDINGS, 5, [a, b, c]);

    analogyResult.innerHTML = nearest
      .map(
        (n, i) =>
          `<span class="${i === 0 ? 'text-accent font-medium' : 'text-muted'}">${n.word.word}</span> <span class="text-xs text-muted">(${n.similarity.toFixed(2)})</span>`
      )
      .join(', ');

    // Add words to visualization if not present
    [embA, embB, embC, nearest[0]?.word].filter(Boolean).forEach((emb) => {
      if (emb && !activeEmbeddings.find((e) => e.word === emb.word)) {
        activeEmbeddings.push(emb);
      }
    });
    updateLegend();
    render();
  }

  // Event handlers
  addWordBtn.addEventListener('click', () => {
    addWord(wordInput.value);
    wordInput.value = '';
  });

  wordInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      addWord(wordInput.value);
      wordInput.value = '';
    }
  });
  wordInput.addEventListener('input', () => {
    setWordFeedback('', 'muted');
  });

  clearAllBtn.addEventListener('click', () => {
    activeEmbeddings = [];
    selectedWord = null;
    loadedPresets.clear();
    updatePresetButtons();
    setWordFeedback('', 'muted');
    updateInfoPanel();
    updateLegend();
    render();
  });

  resetViewBtn.addEventListener('click', () => {
    scale = 200;
    offsetX = 0;
    offsetY = 0;
    render();
  });

  // Preset buttons
  presetButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const preset = btn.getAttribute('data-preset');
      if (preset) {
        loadPreset(preset);
      }
    });
  });

  // Mode selection
  document.querySelectorAll('input[name="mode"]').forEach((radio) => {
    radio.addEventListener('change', (e) => {
      setMode((e.target as HTMLInputElement).value as Mode);
    });
  });

  // Display options
  showLabelsCheckbox.addEventListener('change', () => {
    showLabels = showLabelsCheckbox.checked;
    render();
  });

  showConnectionsCheckbox.addEventListener('change', () => {
    showConnections = showConnectionsCheckbox.checked;
    render();
  });

  colorByCategoryCheckbox.addEventListener('change', () => {
    colorByCategory = colorByCategoryCheckbox.checked;
    render();
  });

  // Analogy
  computeAnalogyBtn.addEventListener('click', computeAnalogy);

  // Canvas interactions
  let activePointerId: number | null = null;

  const getCanvasPoint = (e: PointerEvent) => {
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  canvas.addEventListener('pointerdown', (e) => {
    canvas.focus();
    const { x, y } = getCanvasPoint(e);
    const word = findWordAtPosition(x, y);
    if (word) {
      selectedWord = word;
      updateInfoPanel();
      if (mode === 'similar') {
        showConnections = true;
        showConnectionsCheckbox.checked = true;
      }
    } else {
      isDragging = true;
      dragStartX = e.clientX - offsetX;
      dragStartY = e.clientY - offsetY;
    }
    activePointerId = e.pointerId;
    canvas.setPointerCapture(e.pointerId);
    if (e.pointerType !== 'mouse') {
      hoveredWord = null;
      tooltip.classList.add('hidden');
    }
    render();
  });

  canvas.addEventListener('pointermove', (e) => {
    if (activePointerId !== null && e.pointerId !== activePointerId) return;
    const { x, y } = getCanvasPoint(e);

    if (isDragging) {
      offsetX = e.clientX - dragStartX;
      offsetY = e.clientY - dragStartY;
      render();
      return;
    }

    if (e.pointerType === 'mouse') {
      const word = findWordAtPosition(x, y);
      if (word !== hoveredWord) {
        hoveredWord = word;
        render();
      }

      if (hoveredWord) {
        tooltip.classList.remove('hidden');
        tooltip.style.left = `${x + 15}px`;
        tooltip.style.top = `${y - 10}px`;
        tooltipWord.textContent = hoveredWord.word;
        tooltipCategory.textContent = hoveredWord.category;
      } else {
        tooltip.classList.add('hidden');
      }
    }
  });

  const endPointerDrag = (e: PointerEvent) => {
    if (activePointerId !== null && e.pointerId === activePointerId) {
      activePointerId = null;
      isDragging = false;
      canvas.releasePointerCapture(e.pointerId);
    }
  };

  canvas.addEventListener('pointerup', endPointerDrag);
  canvas.addEventListener('pointercancel', endPointerDrag);
  canvas.addEventListener('pointerleave', (e) => {
    if (e.pointerType === 'mouse') {
      hoveredWord = null;
      tooltip.classList.add('hidden');
      render();
    }
  });

  canvas.addEventListener('wheel', (e) => {
    e.preventDefault();
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    scale *= zoomFactor;
    scale = Math.max(50, Math.min(500, scale));
    render();
  });

  // Keyboard
  canvas.addEventListener('keydown', (e) => {
    if (document.activeElement?.tagName === 'INPUT') return;

    switch (e.key) {
      case '+':
      case '=':
        e.preventDefault();
        scale = Math.min(500, scale * 1.1);
        render();
        break;
      case '-':
        e.preventDefault();
        scale = Math.max(50, scale * 0.9);
        render();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        offsetX += 20;
        render();
        break;
      case 'ArrowRight':
        e.preventDefault();
        offsetX -= 20;
        render();
        break;
      case 'ArrowUp':
        e.preventDefault();
        offsetY += 20;
        render();
        break;
      case 'ArrowDown':
        e.preventDefault();
        offsetY -= 20;
        render();
        break;
      case 'r':
      case 'R':
        e.preventDefault();
        scale = 200;
        offsetX = 0;
        offsetY = 0;
        render();
        break;
      case 'Escape':
        e.preventDefault();
        selectedWord = null;
        updateInfoPanel();
        render();
        break;
      case 'l':
      case 'L':
        e.preventDefault();
        showLabels = !showLabels;
        showLabelsCheckbox.checked = showLabels;
        render();
        break;
    }
  });

  // Initialize
  resizeCanvas();
  window.addEventListener('resize', resizeCanvas);

  // Load a default preset
  loadPreset('animals');
  loadPreset('emotions');
}
